import React from "react";
import { Autocomplete, TextField, CircularProgress, Stack, InputLabel } from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";

interface ControlledAutocompleteProps<T> {
  label?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  options: T[];
  loading: boolean;
  getOptionLabel: (option: T) => string;
  disabled?: boolean;
  clearable?: boolean;
}

export function ControlledAutocomplete<T>({
  label,
  value,
  onChange,
  inputValue,
  onInputChange,
  options,
  loading = false,
  getOptionLabel = (option: any) => option?.toString() || "",
  disabled = false,
  clearable = true,
}: ControlledAutocompleteProps<T>) {
  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "clear") {
          onInputChange("");
          onChange(null);
        } else {
          onInputChange(newInputValue);
        }
      }}
      options={options}
      getOptionLabel={getOptionLabel}
      loading={loading}
      disabled={disabled}
      clearOnEscape={clearable}
      isOptionEqualToValue={(option, val) => getOptionLabel(option) === getOptionLabel(val)}
      renderInput={(params) => (
        <Stack gap={1}>
          <InputLabel htmlFor="autocomplete-search" sx={{ fontSize: "0.9rem" }}>
            {label} *
          </InputLabel>
          <TextField
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <SearchOutlined fontSize="small" sx={{ color: "text.secondary" }} />
                ),
              },
            }}
            {...params}
            variant="outlined"
            name="autocomplete-search"
          />
        </Stack>
      )}
    />
  );
}
